import React from "react";
import { Assert } from "Tools";
import { useEffect } from "react";

export interface RouteAndPageProps<T = any> {
  controllerName: string;
  actionName: string;
  pageProps: T;
}

export interface ControllerRouteTable {
  [K: string]: (props: any) => JSX.Element;
}

export interface ModelStateValues {
  [K: string]: any;
}

export interface ModelStateErrors {
  [K: string]: string[];
}

export function getValuesCaseInsensitive<T>(
  obj: { [K: string]: T },
  key: string,
) {
  return Object.entries(obj)
    .filter(([k]) => k.toLowerCase() === key.toLowerCase())
    .map(([, v]) => v);
}

export function controllerFromRouteTable(routeTable: ControllerRouteTable) {
  return (props: RouteAndPageProps) => {
    const actions = getValuesCaseInsensitive(routeTable, props.actionName);
    Assert.hard(actions.length > 0, `Action ${props.actionName} not found`);
    Assert.hard(
      actions.length < 2,
      `Multiple actions for ${props.actionName} found`,
    );
    const Action = actions[0];
    return <Action {...props.pageProps} />;
  };
}

interface Options {
  isHandlerActive: boolean;
  handleClickOutside: (ev: Event) => void;
  elementRef: React.RefObject<HTMLElement>;
}

export function useClickOutsideHandler({
  isHandlerActive,
  handleClickOutside, // Expected to be static
  elementRef,
}: Options) {
  useEffect(() => {
    if (!isHandlerActive) {
      // No handlers when not active
      return undefined;
    }

    const handleWindowClick = (event: Event) => {
      if (
        !elementRef.current ||
        elementRef.current.contains(event.target as Node)
      ) {
        return;
      }

      handleClickOutside(event);
    };

    window.addEventListener("click", handleWindowClick);
    return () => window.removeEventListener("click", handleWindowClick);
  }, [elementRef, handleClickOutside, isHandlerActive]);
}
